/**
 * Reusable select component
 */

import { forwardRef } from 'react';
import './Select.css';

/**
 * Select component with label and error handling
 * @param {Object} props - Component props
 * @param {string} props.label - Select label
 * @param {Array} props.options - Array of options (strings or {value, label} objects)
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.error - Error message
 * @param {boolean} props.required - Whether field is required
 * @returns {React.ReactElement} Select element
 */
const Select = forwardRef(function Select({
  label,
  options = [],
  placeholder = 'Select an option',
  error,
  required = false,
  className = '',
  id,
  ...props
}, ref) {
  const selectId = id || `select-${label?.toLowerCase().replace(/\s+/g, '-')}`;

  const normalizedOptions = options.map(opt =>
    typeof opt === 'string' ? { value: opt, label: opt } : opt
  );

  return (
    <div className={`select-group ${error ? 'select-group--error' : ''} ${className}`}>
      {label && (
        <label htmlFor={selectId} className="select-group__label">
          {label}
          {required && <span className="select-group__required">*</span>}
        </label>
      )}
      <div className="select-group__wrapper">
        <select
          ref={ref}
          id={selectId}
          className="select-group__select"
          aria-invalid={error ? 'true' : 'false'}
          {...props}
        >
          <option value="">{placeholder}</option>
          {normalizedOptions.map(opt => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <span className="select-group__arrow" />
      </div>
      {error && (
        <span className="select-group__error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
});

export default Select;
